/**
 * `ExternalCvDetail` — pagina di dettaglio arricchito di un profilo CV esterno,
 * raggiungibile dalla pagina "CV esterni" (`ExternalCvList`) cliccando una riga
 * dell'elenco.
 *
 * Rotta `/external-cv/:id` (protetta lato API da `requireLevelsAccess`; la guardia
 * di rotta `LevelsRoute` è difensiva). La pagina gestisce il fetching (dashboard
 * aggregata + dettaglio profilo per il CV Europass + catalogo skill), le azioni
 * (scarica CV, rielabora, elimina) e i relativi modali; il rendering della vista
 * (KPI, competenze, skill, professioni e sezioni del CV) è delegato al componente
 * condiviso {@link ExternalCvProfileView}, riusato anche dalla vista pubblica.
 *
 * Dati:
 *  - `GET /api/external-cv/profiles/:id/dashboard` → {@link ExternalCvDashboardDto}
 *    (KPI, competenze, skill valutate, posizionamento, certificazioni Credly);
 *  - `GET /api/external-cv/profiles/:id` → {@link ExternalCvProfileDetail} per il
 *    CV Europass (`parsedCv`), non incluso nell'aggregato dashboard;
 *  - `GET /api/skills` per le descrizioni skill (hint della tabella).
 * Il download usa `GET /api/external-cv/profiles/:id/cv` → `{ downloadUrl }`;
 * l'eliminazione usa `DELETE /api/external-cv/profiles/:id`.
 *
 * Navigazione: le card competenza/professione/certificazione portano alle rotte
 * di dettaglio dedicate al profilo esterno tramite i `detailPath` passati alla
 * vista condivisa.
 */
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import { ApiRequestError } from '../api/client';
import {
  DEFAULT_SCORING_CONFIG,
  type ExternalCvAiStatus,
  type ExternalCvDashboardDto,
  type ExternalCvProfileDetail,
  type ScoringConfigDto,
  type SkillDto,
} from '../shared/dto';
import { ExternalCvProfileView } from '../components/ExternalCvProfileView';
import { LiveReviewWizard } from './LiveReviewWizard';
import { useAuth } from '../auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  Icon,
  Loading,
  Modal,
  PageHeader,
  TextInput,
  type BadgeTone,
} from '../components/ui';

/** Risposta dell'endpoint di generazione/rigenerazione del Public_Token. */
interface PublicTokenResponse {
  token: string;
}

/** Costruisce l'URL pubblico condivisibile a partire dal Public_Token. */
function buildPublicUrl(token: string): string {
  return `${window.location.origin}/public/submission/${encodeURIComponent(token)}`;
}

/** Risposta dell'endpoint di download del Cv_Pdf. */
interface CvDownloadResponse {
  downloadUrl: string;
}

/** Etichetta e tono della badge per ciascun esito della pre-compilazione AI. */
const AI_STATUS_META: Record<ExternalCvAiStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Pre-compilazione in corso', tone: 'warning' },
  completed: { label: 'Pre-compilazione completata', tone: 'success' },
  failed: { label: 'Pre-compilazione non riuscita', tone: 'danger' },
};

/**
 * Intervallo (ms) di polling mentre l'elaborazione AI del profilo è ancora in
 * corso (`aiStatus`/`parsedCvStatus` a `pending`). L'elaborazione è asincrona
 * (worker SQS): dopo creazione o rielaborazione il profilo resta `pending`
 * finché il worker non completa, quindi la pagina ri-legge periodicamente lo
 * stato e si aggiorna da sola, fermando il polling appena l'esito è definitivo.
 */
const PROCESSING_POLL_INTERVAL_MS = 4000;

export default function ExternalCvDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { role, me } = useAuth();

  // Provenienza opzionale propagata dal chiamante come `state.from`: quando si
  // arriva da un'esplorazione (`/explore/:id`), il tasto "indietro" torna lì
  // invece che all'elenco dei candidati (default). Si accettano solo i path
  // `/explore/` per non interferire con l'ingresso normale da `/external-cv`.
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const backFrom =
    typeof fromState === 'string' && fromState.startsWith('/explore/')
      ? fromState
      : null;
  const backLabel = backFrom !== null ? '← Torna all\'esplorazione' : '← Torna ai profili';

  // Cv_Access lato client (guardia DIFENSIVA): l'autorità resta il backend
  // (`requireCvAccess`). Replica lo stesso predicato usato altrove nel
  // frontend (Navbar, Home, CvRoute): admin oppure `cvEnabled === true`.
  const canReview = role === 'admin' || me?.cvEnabled === true;

  // Visibilità del wizard di review skill dal vivo (Live_Review_Submission).
  const [liveReviewOpen, setLiveReviewOpen] = useState(false);

  // Visibilità dei modali di conferma per le azioni distruttive/costose
  // (eliminazione profilo e rielaborazione), che sostituiscono i `window.confirm`.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmReprocessOpen, setConfirmReprocessOpen] = useState(false);
  // Visibilità del modale di condivisione (genera/rigenera + link condivisibile).
  const [shareOpen, setShareOpen] = useState(false);
  // Token pubblico appena generato in questa sessione: quando presente ha la
  // precedenza sul token già persistito, così l'UI riflette subito la
  // (ri)generazione senza attendere il refetch del dettaglio.
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  // Feedback effimero "Copiato" dopo la copia del link negli appunti.
  const [linkCopied, setLinkCopied] = useState(false);

  const { data, isLoading, isError, error } = useQuery<ExternalCvDashboardDto>({
    queryKey: ['externalCv', 'profiles', 'dashboard', id],
    queryFn: () =>
      api.get<ExternalCvDashboardDto>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/dashboard`,
      ),
    enabled: id !== undefined && id !== '',
    // Un profilo inesistente (404) o un accesso negato (403) non vanno ritentati.
    retry: false,
    // Polling finché la pre-compilazione skill è `pending`: si ferma (ritorna
    // `false`) appena lo stato diventa `completed`/`failed`.
    refetchInterval: (query) =>
      query.state.data?.profile.aiStatus === 'pending'
        ? PROCESSING_POLL_INTERVAL_MS
        : false,
  });

  // Dettaglio profilo: la dashboard non include il CV Europass (`parsedCv`), che
  // vive su `ExternalCvProfileDetail`. Lo carichiamo con una query dedicata a
  // `/profiles/:id`. Un fallimento qui non deve rompere la pagina: le altre
  // sezioni restano funzionanti (gestito passando `cvError` alla vista).
  const detailQuery = useQuery<ExternalCvProfileDetail>({
    queryKey: ['externalCv', 'profiles', 'detail', id],
    queryFn: () =>
      api.get<ExternalCvProfileDetail>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}`,
      ),
    enabled: id !== undefined && id !== '',
    retry: false,
    // Polling finché l'estrazione Europass è `pending`: si ferma appena lo stato
    // diventa `completed`/`failed`.
    refetchInterval: (query) =>
      query.state.data?.parsedCvStatus === 'pending'
        ? PROCESSING_POLL_INTERVAL_MS
        : false,
  });

  // Catalogo skill: serve solo a recuperare la descrizione (assente in
  // `AssessmentView`) da mostrare nell'hint accanto al nome skill.
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Configurazione di scoring: alimenta il dettaglio del calcolo dello Score
  // (apribile cliccando il badge Score di una skill). In attesa/errore ripiega
  // sui default applicativi, come nelle altre viste che espongono il dettaglio.
  const scoringConfigQuery = useQuery({
    queryKey: ['settings', 'scoring'],
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
    staleTime: 5 * 60 * 1000,
  });
  const scoringConfig = scoringConfigQuery.data ?? DEFAULT_SCORING_CONFIG;
  const descriptionBySkillId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const s of skillsQuery.data ?? []) map.set(s.id, s.description);
    return map;
  }, [skillsQuery.data]);

  // Ritorno del tasto "indietro": all'esplorazione di provenienza se presente,
  // altrimenti all'elenco dei candidati.
  const backToList = () => {
    void navigate(backFrom ?? '/external-cv');
  };

  // Dopo l'eliminazione il profilo non esiste più: si torna sempre all'elenco
  // dei candidati, a prescindere dalla provenienza.
  const afterDelete = () => {
    void navigate('/external-cv');
  };

  // Download del Cv_Pdf: ottiene un URL firmato e lo apre in una nuova scheda.
  const downloadCv = useMutation({
    mutationFn: () =>
      api.get<CvDownloadResponse>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/cv`,
      ),
    onSuccess: (result) => {
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  // Eliminazione del profilo: alla conferma rimuove il profilo e torna all'elenco.
  const deleteProfile = useMutation({
    mutationFn: () =>
      api.delete<void>(`/api/external-cv/profiles/${encodeURIComponent(id ?? '')}`),
    onSuccess: () => {
      setConfirmDeleteOpen(false);
      afterDelete();
    },
  });

  // Ricalcolo del profilo: rilancia l'intera analisi AI sul CV già caricato
  // (parsing skill, estrazione Europass e ricerca certificazioni), sovrascrivendo
  // completamente i dati importati. Il profilo torna `pending`; alla conferma
  // invalidiamo dashboard, dettaglio ed elenco per rileggere lo stato aggiornato.
  const reprocessCv = useMutation({
    mutationFn: () =>
      api.post<ExternalCvProfileDetail>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/reprocess`,
      ),
    onSuccess: () => {
      setConfirmReprocessOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ['externalCv', 'profiles', 'dashboard', id],
      });
      void queryClient.invalidateQueries({
        queryKey: ['externalCv', 'profiles', 'detail', id],
      });
      void queryClient.invalidateQueries({ queryKey: ['externalCv', 'profiles'] });
    },
  });

  // Generazione/rigenerazione del token pubblico condivisibile del profilo.
  // Rigenerare invalida il token precedente lato server (il link vecchio smette
  // di funzionare) e riattiva il link. Al successo teniamo il token in stato
  // locale (mostrato subito) e invalidiamo il dettaglio per riallineare il DTO.
  const generatePublicLink = useMutation({
    mutationFn: () =>
      api.post<PublicTokenResponse>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/public-token`,
      ),
    onSuccess: (result) => {
      setGeneratedToken(result.token);
      setLinkCopied(false);
      void queryClient.invalidateQueries({
        queryKey: ['externalCv', 'profiles', 'detail', id],
      });
    },
  });

  // Token effettivo da mostrare: quello appena generato, altrimenti quello già
  // persistito sul profilo (se presente).
  const publicToken = generatedToken ?? detailQuery.data?.publicToken ?? null;
  const publicUrl = publicToken !== null ? buildPublicUrl(publicToken) : null;

  const copyPublicUrl = async () => {
    if (publicUrl === null) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard non disponibile (es. contesto non sicuro): nessuna azione.
    }
  };

  if (isLoading) {
    return (
      <section aria-labelledby="external-cv-detail-title">
        <PageHeader
          title="Dettaglio profilo"
          titleId="external-cv-detail-title"
          subtitle="Dettaglio del profilo CV esterno."
          actions={
            <Button type="button" variant="ghost" onClick={backToList}>
              {backLabel}
            </Button>
          }
        />
        <Loading>Caricamento del profilo…</Loading>
      </section>
    );
  }

  if (isError || data === undefined) {
    const notFound = error instanceof ApiRequestError && error.kind === 'not_found';
    // Il `403` è trattato come accesso negato (guardia difensiva; l'API è
    // l'autorità autorevole sull'autorizzazione).
    const forbidden = error instanceof ApiRequestError && error.kind === 'forbidden';
    const message = notFound
      ? 'Il profilo richiesto non esiste o è stato eliminato.'
      : forbidden
        ? 'Non hai i permessi per consultare questo profilo.'
        : error instanceof Error
          ? error.message
          : 'Impossibile caricare il profilo.';
    return (
      <section aria-labelledby="external-cv-detail-title">
        <PageHeader
          title="Dettaglio profilo"
          titleId="external-cv-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToList}>
              {backLabel}
            </Button>
          }
        />
        <Alert tone={notFound || forbidden ? 'warning' : 'error'}>{message}</Alert>
      </section>
    );
  }

  const { profile } = data;
  const status = AI_STATUS_META[profile.aiStatus];
  const fullName = `${profile.firstName} ${profile.lastName}`.trim();

  // `id` è una stringa valida qui (le query sono `enabled` solo con `id`).
  const profileId = encodeURIComponent(id ?? '');
  const competenceDetailPath = (competenceId: string) =>
    `/external-cv/${profileId}/competences/${competenceId}`;
  const jobDetailPath = (jobId: string) => `/external-cv/${profileId}/jobs/${jobId}`;
  const certificationDetailPath = (certificationId: string) =>
    `/external-cv/${profileId}/certifications/${encodeURIComponent(certificationId)}`;

  // CV Europass estratto, disponibile solo a estrazione completata.
  const parsedCv =
    detailQuery.data?.parsedCvStatus === 'completed'
      ? (detailQuery.data.parsedCv ?? null)
      : null;
  const parsedCvStatus = detailQuery.data?.parsedCvStatus ?? 'pending';

  const header = (
    <PageHeader
      title={fullName}
      titleId="external-cv-detail-title"
      subtitle="Dettaglio del profilo CV esterno derivato dalle valutazioni AI."
      titleAdornment={<Badge tone={status.tone}>{status.label}</Badge>}
      actions={
        <div className="external-cv-detail__actions">
          <Button type="button" variant="ghost" onClick={backToList}>
            {backLabel}
          </Button>
          {/* Comando di avvio della review skill dal vivo: montato solo con
              Levels_Access (guardia difensiva via `useAuth`). Il dettaglio
              interno è l'unico contesto in cui `ExternalCvDetail` è usato: la
              vista pubblica monta direttamente `ExternalCvProfileView`
              (`PublicCvDashboard`), perciò questo comando non compare mai lì. */}
          {canReview && (
            <Button
              type="button"
              iconOnly
              onClick={() => setLiveReviewOpen(true)}
              aria-label="Avvia review skill"
              title="Avvia review skill"
            >
              <Icon name="award" title="Avvia review skill" />
            </Button>
          )}
          <Button
            type="button"
            iconOnly
            onClick={() => setConfirmReprocessOpen(true)}
            loading={reprocessCv.isPending}
            aria-label="Rielabora"
            title="Rielabora"
          >
            <Icon name="sparkle" title="Rielabora" />
          </Button>
          <Button
            type="button"
            iconOnly
            onClick={() => setShareOpen(true)}
            aria-label="Condividi"
            title="Condividi"
          >
            <Icon name="external" title="Condividi" />
          </Button>
          <Button
            type="button"
            iconOnly
            onClick={() => downloadCv.mutate()}
            loading={downloadCv.isPending}
            aria-label="Scarica CV"
            title="Scarica CV"
          >
            <Icon name="download" title="Scarica CV" />
          </Button>
          <Button
            type="button"
            variant="danger"
            iconOnly
            className="ui-btn--danger-solid"
            onClick={() => setConfirmDeleteOpen(true)}
            loading={deleteProfile.isPending}
            aria-label="Elimina"
            title="Elimina"
          >
            <Icon name="trash" title="Elimina" />
          </Button>
        </div>
      }
    />
  );

  // Alert delle azioni (mostrati tra header e sezioni tramite lo slot `banner`).
  const banner = (
    <>
      {downloadCv.isError && (
        <Alert tone="error">
          {downloadCv.error instanceof Error
            ? downloadCv.error.message
            : 'Impossibile ottenere il link di download del CV.'}
        </Alert>
      )}
      {deleteProfile.isError && (
        <Alert tone="error">
          {deleteProfile.error instanceof Error
            ? deleteProfile.error.message
            : 'Impossibile eliminare il profilo.'}
        </Alert>
      )}
      {reprocessCv.isError && (
        <Alert tone="error">
          {reprocessCv.error instanceof Error
            ? reprocessCv.error.message
            : 'Impossibile avviare la rielaborazione del profilo.'}
        </Alert>
      )}
    </>
  );

  // Errore di caricamento del CV strutturato: sostituisce le sezioni del CV.
  const cvError = detailQuery.isError ? (
    <Alert tone="error">
      {detailQuery.error instanceof Error
        ? detailQuery.error.message
        : 'Impossibile caricare il CV strutturato.'}
    </Alert>
  ) : undefined;

  return (
    <>
      <ExternalCvProfileView
        header={header}
        banner={banner}
        dashboard={data.dashboard}
        assessments={data.assessments}
        jobPositioning={data.jobPositioning}
        certifications={data.certifications ?? []}
        parsedCv={parsedCv}
        parsedCvStatus={parsedCvStatus}
        cvLoading={detailQuery.isLoading}
        cvError={cvError}
        descriptionBySkillId={descriptionBySkillId}
        scoringConfig={scoringConfig}
        competenceDetailPath={competenceDetailPath}
        jobDetailPath={jobDetailPath}
        certificationDetailPath={certificationDetailPath}
      />

      {/* Wizard di review skill dal vivo: montato solo con Levels_Access
          (guardia difensiva). Apre il `LiveReviewWizard` per il profilo corrente.
          `id` è una stringa valida qui (le query sono `enabled` solo con `id`). */}
      {canReview && (
        <LiveReviewWizard
          profileId={id ?? ''}
          open={liveReviewOpen}
          onClose={() => setLiveReviewOpen(false)}
        />
      )}

      {/* Condivisione: genera/rigenera il link pubblico e permette di copiarlo.
          Tutta la gestione del Public_Token vive qui, dietro il tasto "Condividi". */}
      <Modal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Condividi il profilo"
        footer={
          <Button type="button" variant="ghost" onClick={() => setShareOpen(false)}>
            Chiudi
          </Button>
        }
      >
        <div className="external-cv-detail__share">
          <p>
            Genera un link pubblico per condividere questo profilo. Chi lo riceve può
            consultarlo senza autenticazione.
            {publicToken !== null &&
              ' Rigenerando il link, quello precedente smette di funzionare.'}
          </p>

          {generatePublicLink.isError && (
            <Alert tone="error">
              {generatePublicLink.error instanceof Error
                ? generatePublicLink.error.message
                : 'Impossibile generare il link pubblico del profilo.'}
            </Alert>
          )}

          {publicUrl !== null && (
            <div className="public-url-field">
              <TextInput
                id="external-cv-public-url"
                type="text"
                value={publicUrl}
                readOnly
                aria-label="Link pubblico condivisibile del profilo"
              />
              <Button
                type="button"
                variant="ghost"
                iconOnly
                onClick={() => void copyPublicUrl()}
                aria-label={linkCopied ? 'Copiato' : 'Copia link'}
                title={linkCopied ? 'Copiato' : 'Copia link'}
              >
                <Icon
                  name={linkCopied ? 'check' : 'copy'}
                  title={linkCopied ? 'Copiato' : 'Copia link'}
                />
              </Button>
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            onClick={() => generatePublicLink.mutate()}
            loading={generatePublicLink.isPending}
          >
            {publicToken !== null ? 'Rigenera link' : 'Genera link'}
          </Button>
        </div>
      </Modal>

      {/* Conferma della rielaborazione: azione costosa e sovrascrittura totale
          dei dati importati, quindi passa da un modale esplicito. */}
      <Modal
        open={confirmReprocessOpen}
        onClose={() => setConfirmReprocessOpen(false)}
        title="Rielaborare il profilo?"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmReprocessOpen(false)}
              disabled={reprocessCv.isPending}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => reprocessCv.mutate()}
              loading={reprocessCv.isPending}
            >
              Rielabora
            </Button>
          </>
        }
      >
        <p>
          Il profilo verrà ricalcolato dal CV già caricato. Valutazioni, CV estratto
          e certificazioni importate saranno rigenerati, sovrascrivendo completamente
          i dati attuali.
        </p>
      </Modal>

      {/* Conferma dell'eliminazione: azione irreversibile, gestita da un modale
          con pulsante di conferma "danger". */}
      <Modal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Eliminare il profilo?"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={deleteProfile.isPending}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="danger"
              className="ui-btn--danger-solid"
              onClick={() => deleteProfile.mutate()}
              loading={deleteProfile.isPending}
            >
              Elimina
            </Button>
          </>
        }
      >
        <p>
          Il profilo di <strong>{fullName}</strong> verrà eliminato definitivamente,
          insieme al CV e alle valutazioni associate. L'operazione non è reversibile.
        </p>
      </Modal>
    </>
  );
}
